/**
 * The book theme (decisions 16, 17; ADR 2): named settings that drive the preview, EPUB, PDF and DOCX alike,
 * with no custom CSS. Part 05 log, table T, lists every setting and how each output reads it.
 *
 * Two principles shape the fields. Theme settings merge last-writer-wins one field at a time (decision 62),
 * so every combination of individually valid values is a valid theme: no rule relates two fields (T0).
 * And every value has an exact form in every output (ADR 2), so sizes are whole half-points (DOCX's font
 * unit), indents whole tenths of an em (whole twips at every size), line heights whole multiples of 5 %.
 */
import { FONT_IDS } from './fonts.ts';
import type { FontId } from './fonts.ts';
import { booleanField, choiceField, frozen, integerField, parseRecordJson, readFields } from './record.ts';
import { RecordError } from './record.ts';
import type { Fields } from './record.ts';

/** Paragraph alignment. */
export type Alignment = 'justify' | 'left';
/** How a chapter title's letters are set. */
export type TitleStyle = 'normal' | 'uppercase' | 'small-caps';
/** Where a chapter's image goes against its heading (T‴3). */
export type HeadingImagePlace = 'above' | 'between' | 'below' | 'behind';
/** How a heading line sits across the page (table T″4). */
export type HeadingAlign = 'left' | 'center' | 'right';
/** The number shown above each chapter title: `word` is "One", `chapter-word` "Chapter One", `chapter-arabic` "Chapter 1" (T″2). */
export type ChapterNumbering = 'none' | 'arabic' | 'roman' | 'word' | 'chapter-word' | 'chapter-arabic';
/** What a scene break (`***`) shows as. */
export type SceneBreakStyle = 'asterisks' | 'blank';

/** A book theme. Sizes are in half-points (22 = 11 pt), indents and spacing in tenths of an em of the body size. */
export interface Theme {
  readonly bodyFont: FontId;
  readonly headingFont: FontId;
  /** Body text size, half-points. */
  readonly bodySize: number;
  /** Line height, percent of the body size. */
  readonly lineHeight: number;
  readonly alignment: Alignment;
  readonly hyphenate: boolean;
  /** Indent of a paragraph's first line, tenths of an em. */
  readonly firstLineIndent: number;
  /** Extra space between paragraphs, tenths of an em. */
  readonly paragraphSpacing: number;
  /** Whether a paragraph right after a chapter title, heading or scene break is indented too. */
  readonly indentAfterBreak: boolean;
  /** Indent of a block quote on both sides, tenths of an em. */
  readonly quoteIndent: number;
  /** Whether chapters show a heading at all (T″1). */
  readonly chapterHeading: boolean;
  readonly chapterNumbering: ChapterNumbering;
  readonly chapterNumberFont: FontId;
  readonly chapterNumberAlign: HeadingAlign;
  readonly chapterNumberBold: boolean;
  readonly chapterNumberItalic: boolean;
  readonly chapterNumberStyle: TitleStyle;
  /** Chapter number size, half-points. */
  readonly chapterNumberSize: number;
  /** How far the line fades towards the paper, percent; {@link MAX_FADE} at most, so it can never vanish (T″8). */
  readonly chapterNumberFade: number;
  readonly chapterTitleFont: FontId;
  readonly chapterTitleAlign: HeadingAlign;
  readonly chapterTitleBold: boolean;
  readonly chapterTitleItalic: boolean;
  readonly chapterTitleStyle: TitleStyle;
  /** Chapter title size, half-points. */
  readonly chapterTitleSize: number;
  readonly chapterTitleFade: number;
  /** Space between the number and the title, tenths of an em (T″9). */
  readonly chapterTitleGap: number;
  readonly chapterSubtitleFont: FontId;
  readonly chapterSubtitleAlign: HeadingAlign;
  readonly chapterSubtitleBold: boolean;
  readonly chapterSubtitleItalic: boolean;
  readonly chapterSubtitleStyle: TitleStyle;
  /** Chapter subtitle size, half-points. */
  readonly chapterSubtitleSize: number;
  readonly chapterSubtitleFade: number;
  /** Space between the title and the subtitle, tenths of an em (T″9). */
  readonly chapterSubtitleGap: number;
  /** The book's chapter image: an image reference as the text writes it, or empty for none (T‴1). */
  readonly chapterImage: string;
  /** Whether each chapter names its own image instead (T‴2). */
  readonly chapterImagePerChapter: boolean;
  readonly chapterImagePlace: HeadingImagePlace;
  readonly chapterImageAlign: HeadingAlign;
  /** How far the image fades, percent (T‴5). */
  readonly chapterImageFade: number;
  /** Its width, percent of the text's (T‴6). */
  readonly chapterImageSize: number;
  /** Whether it runs off the paper's edge; print only, so only the PDF's pages show it (T‴7). */
  readonly chapterImageBleed: boolean;
  /** Size of a heading inside a chapter (`#` and deeper, decision 35), half-points. */
  readonly subheadingSize: number;
  readonly sceneBreak: SceneBreakStyle;
  /** Footnote text size, half-points. */
  readonly footnoteSize: number;
  /** Whether a paragraph's first or last line may never sit alone on a page. */
  readonly widowControl: boolean;
}

/** The unit an integer setting is counted in. */
export type ThemeUnit = 'half-point' | 'percent' | 'tenth-em';

/** One setting's allowed values: the settings screen builds its controls from these. */
export type ThemeField =
  | { readonly kind: 'integer'; readonly unit: ThemeUnit; readonly min: number; readonly max: number; readonly step: number }
  | { readonly kind: 'choice'; readonly values: readonly string[] }
  | { readonly kind: 'boolean' }
  | { readonly kind: 'image' };

const font = { kind: 'choice', values: FONT_IDS } as const;
const flag = { kind: 'boolean' } as const;
const halfPoints = (min: number, max: number) => ({ kind: 'integer', unit: 'half-point', min, max, step: 1 }) as const;
const aligned = { kind: 'choice', values: ['left', 'center', 'right'] } as const;
const styled = { kind: 'choice', values: ['normal', 'uppercase', 'small-caps'] } as const;
/** T″8: the most a heading line can fade: it stays readable and can never vanish. */
export const MAX_FADE = 70;
const fade = { kind: 'integer', unit: 'percent', min: 0, max: MAX_FADE, step: 5 } as const;
const tenthsEm = (max: number) => ({ kind: 'integer', unit: 'tenth-em', min: 0, max, step: 1 }) as const;

/**
 * Every setting, in the order a serialised theme lists them (table T). The record reader refuses any key or
 * value not listed here (V3, V7, V10), so adding a setting, widening a range or adding a choice is a
 * book-format change (V14): it ships with a new book-format version under decision 65's migration rule, never
 * as a silent edit, or a device on the previous build could no longer read the book's record.
 */
export const THEME_FIELDS = frozen({
  bodyFont: font,
  headingFont: font,
  bodySize: halfPoints(16, 32),
  lineHeight: { kind: 'integer', unit: 'percent', min: 100, max: 200, step: 5 },
  alignment: { kind: 'choice', values: ['justify', 'left'] },
  hyphenate: flag,
  firstLineIndent: tenthsEm(40),
  paragraphSpacing: tenthsEm(20),
  indentAfterBreak: flag,
  quoteIndent: tenthsEm(40),
  chapterHeading: flag,
  chapterNumbering: { kind: 'choice', values: ['none', 'arabic', 'roman', 'word', 'chapter-word', 'chapter-arabic'] },
  chapterNumberFont: font,
  chapterNumberAlign: aligned,
  chapterNumberBold: flag,
  chapterNumberItalic: flag,
  chapterNumberStyle: styled,
  chapterNumberSize: halfPoints(16, 96),
  chapterNumberFade: fade,
  chapterTitleFont: font,
  chapterTitleAlign: aligned,
  chapterTitleBold: flag,
  chapterTitleItalic: flag,
  chapterTitleStyle: styled,
  chapterTitleSize: halfPoints(20, 96),
  chapterTitleFade: fade,
  chapterTitleGap: tenthsEm(40),
  chapterSubtitleFont: font,
  chapterSubtitleAlign: aligned,
  chapterSubtitleBold: flag,
  chapterSubtitleItalic: flag,
  chapterSubtitleStyle: styled,
  chapterSubtitleSize: halfPoints(16, 96),
  chapterSubtitleFade: fade,
  chapterSubtitleGap: tenthsEm(40),
  chapterImage: { kind: 'image' },
  chapterImagePerChapter: flag,
  chapterImagePlace: { kind: 'choice', values: ['above', 'between', 'below', 'behind'] },
  chapterImageAlign: aligned,
  chapterImageFade: { kind: 'integer', unit: 'percent', min: 0, max: 90, step: 5 },
  chapterImageSize: { kind: 'integer', unit: 'percent', min: 10, max: 100, step: 5 },
  chapterImageBleed: flag,
  subheadingSize: halfPoints(16, 64),
  sceneBreak: { kind: 'choice', values: ['asterisks', 'blank'] },
  footnoteSize: halfPoints(12, 28),
  widowControl: flag,
} as const satisfies Readonly<Record<keyof Theme, ThemeField>>);

/** The settings' names in serialised order. */
export const THEME_KEYS: readonly (keyof Theme)[] = frozen([
  'bodyFont',
  'headingFont',
  'bodySize',
  'lineHeight',
  'alignment',
  'hyphenate',
  'firstLineIndent',
  'paragraphSpacing',
  'indentAfterBreak',
  'quoteIndent',
  'chapterHeading',
  'chapterNumbering',
  'chapterNumberFont',
  'chapterNumberAlign',
  'chapterNumberBold',
  'chapterNumberItalic',
  'chapterNumberStyle',
  'chapterNumberSize',
  'chapterNumberFade',
  'chapterTitleFont',
  'chapterTitleAlign',
  'chapterTitleBold',
  'chapterTitleItalic',
  'chapterTitleStyle',
  'chapterTitleSize',
  'chapterTitleFade',
  'chapterTitleGap',
  'chapterSubtitleFont',
  'chapterSubtitleAlign',
  'chapterSubtitleBold',
  'chapterSubtitleItalic',
  'chapterSubtitleStyle',
  'chapterSubtitleSize',
  'chapterSubtitleFade',
  'chapterSubtitleGap',
  'chapterImage',
  'chapterImagePerChapter',
  'chapterImagePlace',
  'chapterImageAlign',
  'chapterImageFade',
  'chapterImageSize',
  'chapterImageBleed',
  'subheadingSize',
  'sceneBreak',
  'footnoteSize',
  'widowControl',
]);

/** The chapter fields every older record has, so only the rest read as {@link olderLook}. */
const OLDER_KEYS: ReadonlySet<keyof Theme> = new Set<keyof Theme>(['chapterNumbering', 'chapterTitleSize', 'chapterTitleAlign', 'chapterTitleStyle']);

type IntegerKey = { [K in keyof typeof THEME_FIELDS]: (typeof THEME_FIELDS)[K]['kind'] extends 'integer' ? K : never }[keyof typeof THEME_FIELDS];
type FlagKey = { [K in keyof typeof THEME_FIELDS]: (typeof THEME_FIELDS)[K]['kind'] extends 'boolean' ? K : never }[keyof typeof THEME_FIELDS];

/** The theme a new book starts with (the "classic" preset). */
export const DEFAULT_THEME: Theme = frozen({
  bodyFont: 'source-serif-4',
  headingFont: 'source-serif-4',
  bodySize: 22,
  lineHeight: 135,
  alignment: 'justify',
  hyphenate: true,
  firstLineIndent: 15,
  paragraphSpacing: 0,
  indentAfterBreak: false,
  quoteIndent: 20,
  chapterHeading: true,
  chapterNumbering: 'none',
  chapterNumberFont: 'source-serif-4',
  chapterNumberAlign: 'center',
  chapterNumberBold: false,
  chapterNumberItalic: false,
  chapterNumberStyle: 'normal',
  chapterNumberSize: 26,
  chapterNumberFade: 0,
  chapterTitleFont: 'source-serif-4',
  chapterTitleAlign: 'center',
  chapterTitleBold: false,
  chapterTitleItalic: false,
  chapterTitleStyle: 'normal',
  chapterTitleSize: 40,
  chapterTitleFade: 0,
  chapterTitleGap: 0,
  chapterSubtitleFont: 'source-serif-4',
  chapterSubtitleAlign: 'center',
  chapterSubtitleBold: false,
  chapterSubtitleItalic: false,
  chapterSubtitleStyle: 'normal',
  chapterSubtitleSize: 26,
  chapterSubtitleFade: 0,
  chapterSubtitleGap: 0,
  chapterImage: '',
  chapterImagePerChapter: false,
  chapterImagePlace: 'above',
  chapterImageAlign: 'center',
  chapterImageFade: 0,
  chapterImageSize: 60,
  chapterImageBleed: false,
  subheadingSize: 26,
  sceneBreak: 'asterisks',
  footnoteSize: 18,
  widowControl: true,
});

/** The three heading lines' fonts, set alike: what a preset names once. */
function headingFonts(font: FontId): Pick<Theme, 'chapterNumberFont' | 'chapterTitleFont' | 'chapterSubtitleFont'> {
  return { chapterNumberFont: font, chapterTitleFont: font, chapterSubtitleFont: font };
}

/** A book styling preset's ID (decision 16). */
export type PresetId = 'classic' | 'elegant' | 'modern' | 'large-print';

/** The book styling presets (decision 16): whole themes the writer starts from and may then change. */
export const THEME_PRESETS: Readonly<Record<PresetId, Theme>> = frozen({
  classic: DEFAULT_THEME,
  elegant: {
    ...DEFAULT_THEME,
    bodyFont: 'eb-garamond',
    headingFont: 'eb-garamond',
    ...headingFonts('eb-garamond'),
    bodySize: 23,
    lineHeight: 140,
    firstLineIndent: 12,
    chapterTitleSize: 36,
    chapterTitleStyle: 'small-caps',
    chapterNumbering: 'roman',
  },
  modern: {
    ...DEFAULT_THEME,
    bodyFont: 'literata',
    headingFont: 'source-sans-3',
    ...headingFonts('source-sans-3'),
    bodySize: 21,
    lineHeight: 150,
    alignment: 'left',
    hyphenate: false,
    firstLineIndent: 0,
    paragraphSpacing: 8,
    chapterTitleSize: 44,
    chapterNumberAlign: 'left',
    chapterTitleAlign: 'left',
    chapterSubtitleAlign: 'left',
    chapterNumbering: 'arabic',
    chapterNumberSize: 28,
    chapterSubtitleSize: 28,
    subheadingSize: 28,
  },
  'large-print': {
    ...DEFAULT_THEME,
    bodyFont: 'pt-serif',
    headingFont: 'pt-serif',
    ...headingFonts('pt-serif'),
    bodySize: 32,
    lineHeight: 150,
    alignment: 'left',
    hyphenate: false,
    firstLineIndent: 20,
    chapterTitleSize: 56,
    chapterNumberSize: 40,
    chapterSubtitleSize: 40,
    subheadingSize: 40,
    footnoteSize: 28,
  },
});

/** Marks a field an older record doesn't have, so {@link parseTheme} can give it the look that record drew. */
const ABSENT = Symbol('absent');
const HEADING_FIELDS: readonly (keyof Theme)[] = THEME_KEYS.filter((key) => key.startsWith('chapter') && !OLDER_KEYS.has(key));
const ABSENT_FIELDS: ReadonlyMap<string, unknown> = new Map(HEADING_FIELDS.map((key) => [key, ABSENT]));

/** T″: what a field an older record lacks reads as: the look that record drew (the number took the heading font and the subheading size). */
function olderLook(fields: Fields, key: keyof Theme): unknown {
  if (key === 'chapterHeading') return true;
  if (key.startsWith('chapterImage')) return DEFAULT_THEME[key];
  if (key.endsWith('Font')) return fields.get('headingFont');
  if (key.endsWith('Align')) return fields.get('chapterTitleAlign');
  if (key.endsWith('Size')) return fields.get('subheadingSize');
  if (key.endsWith('Style')) return 'normal';
  if (key.endsWith('Bold') || key.endsWith('Italic')) return false;
  return 0;
}

/** The record's fields with each absent one filled in by {@link olderLook}, checked after like any other (T″). */
function withOlderLook(fields: Fields): Fields {
  const filled = new Map(fields);
  for (const key of HEADING_FIELDS) if (fields.get(key) === ABSENT) filled.set(key, olderLook(fields, key));
  return filled;
}

/** T‴1: an image reference as `imageDestination` writes it; nothing else can name a file. */
const IMAGE_REFERENCE = /^image:[0-9a-f]{32}\?sha256=[0-9a-f]{64}$/u;

function imageField(fields: Fields): string {
  const value = fields.get('chapterImage');
  if (value !== '' && (typeof value !== 'string' || !IMAGE_REFERENCE.test(value))) throw new RecordError('chapterImage', 'must be an image reference or empty');
  return value;
}

/** A theme record's value to a {@link Theme}, or a `RecordError` naming the first field that is wrong (table V); an older record reads as it drew (T″). */
export function parseTheme(value: unknown): Theme {
  const fields = withOlderLook(readFields(value, THEME_KEYS, 'theme', null, ABSENT_FIELDS));
  const f = THEME_FIELDS;
  const number = (key: IntegerKey): number => integerField(fields, key, f[key].min, f[key].max, f[key].step);
  const flagOf = (key: FlagKey): boolean => booleanField(fields, key);
  // An older record's new fields are copies of these two: checked first, so an error names the field the record holds.
  const subheadingSize = number('subheadingSize');
  const chapterTitleAlign = choiceField(fields, 'chapterTitleAlign', f.chapterTitleAlign.values);
  return {
    bodyFont: choiceField(fields, 'bodyFont', f.bodyFont.values),
    headingFont: choiceField(fields, 'headingFont', f.headingFont.values),
    bodySize: number('bodySize'),
    lineHeight: number('lineHeight'),
    alignment: choiceField(fields, 'alignment', f.alignment.values),
    hyphenate: flagOf('hyphenate'),
    firstLineIndent: number('firstLineIndent'),
    paragraphSpacing: number('paragraphSpacing'),
    indentAfterBreak: flagOf('indentAfterBreak'),
    quoteIndent: number('quoteIndent'),
    chapterHeading: flagOf('chapterHeading'),
    chapterNumbering: choiceField(fields, 'chapterNumbering', f.chapterNumbering.values),
    chapterNumberFont: choiceField(fields, 'chapterNumberFont', f.chapterNumberFont.values),
    chapterNumberAlign: choiceField(fields, 'chapterNumberAlign', f.chapterNumberAlign.values),
    chapterNumberBold: flagOf('chapterNumberBold'),
    chapterNumberItalic: flagOf('chapterNumberItalic'),
    chapterNumberStyle: choiceField(fields, 'chapterNumberStyle', f.chapterNumberStyle.values),
    chapterNumberSize: number('chapterNumberSize'),
    chapterNumberFade: number('chapterNumberFade'),
    chapterTitleFont: choiceField(fields, 'chapterTitleFont', f.chapterTitleFont.values),
    chapterTitleAlign,
    chapterTitleBold: flagOf('chapterTitleBold'),
    chapterTitleItalic: flagOf('chapterTitleItalic'),
    chapterTitleStyle: choiceField(fields, 'chapterTitleStyle', f.chapterTitleStyle.values),
    chapterTitleSize: number('chapterTitleSize'),
    chapterTitleFade: number('chapterTitleFade'),
    chapterTitleGap: number('chapterTitleGap'),
    chapterSubtitleFont: choiceField(fields, 'chapterSubtitleFont', f.chapterSubtitleFont.values),
    chapterSubtitleAlign: choiceField(fields, 'chapterSubtitleAlign', f.chapterSubtitleAlign.values),
    chapterSubtitleBold: flagOf('chapterSubtitleBold'),
    chapterSubtitleItalic: flagOf('chapterSubtitleItalic'),
    chapterSubtitleStyle: choiceField(fields, 'chapterSubtitleStyle', f.chapterSubtitleStyle.values),
    chapterSubtitleSize: number('chapterSubtitleSize'),
    chapterSubtitleFade: number('chapterSubtitleFade'),
    chapterSubtitleGap: number('chapterSubtitleGap'),
    chapterImage: imageField(fields),
    chapterImagePerChapter: flagOf('chapterImagePerChapter'),
    chapterImagePlace: choiceField(fields, 'chapterImagePlace', f.chapterImagePlace.values),
    chapterImageAlign: choiceField(fields, 'chapterImageAlign', f.chapterImageAlign.values),
    chapterImageFade: number('chapterImageFade'),
    chapterImageSize: number('chapterImageSize'),
    chapterImageBleed: flagOf('chapterImageBleed'),
    subheadingSize,
    sceneBreak: choiceField(fields, 'sceneBreak', f.sceneBreak.values),
    footnoteSize: number('footnoteSize'),
    widowControl: flagOf('widowControl'),
  };
}

/**
 * A theme as JSON, fields in {@link THEME_KEYS} order, so equal themes serialise to equal text. It reads the
 * theme's fields by name into a fresh object (W2: a UI store's proxy, a frozen constant or a class instance
 * all serialise alike), checks that with {@link parseTheme} and throws its `RecordError`, so nothing is
 * written that can't be read back (W1).
 */
export function serializeTheme(theme: Theme): string {
  const copy = Object.fromEntries(THEME_KEYS.map((key) => [key, theme[key]]));
  // parseTheme builds its result in THEME_KEYS order.
  return JSON.stringify(parseTheme(copy));
}

/** Serialised theme text to a {@link Theme}; `RecordError` for anything {@link serializeTheme} can't have written. */
export function themeFromJson(text: string): Theme {
  return parseTheme(parseRecordJson(text, 'theme'));
}

/** One heading line's look, whichever line it is: what every output reads (T″). */
export interface HeadingLook {
  readonly font: FontId;
  readonly align: HeadingAlign;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly style: TitleStyle;
  /** Half-points. */
  readonly size: number;
  /** Percent, up to {@link MAX_FADE}. */
  readonly fade: number;
  /** Space above the line, tenths of an em; the number has none. */
  readonly gap: number;
}

/** A chapter heading's three lines, in the order they are set. */
export interface HeadingLooks {
  readonly number: HeadingLook;
  readonly title: HeadingLook;
  readonly subtitle: HeadingLook;
}

/** T″: the theme's flat heading fields as one look per line, so no output reads them by name. */
export function headingLooks(theme: Theme): HeadingLooks {
  return {
    number: { font: theme.chapterNumberFont, align: theme.chapterNumberAlign, bold: theme.chapterNumberBold, italic: theme.chapterNumberItalic, style: theme.chapterNumberStyle, size: theme.chapterNumberSize, fade: theme.chapterNumberFade, gap: 0 },
    title: { font: theme.chapterTitleFont, align: theme.chapterTitleAlign, bold: theme.chapterTitleBold, italic: theme.chapterTitleItalic, style: theme.chapterTitleStyle, size: theme.chapterTitleSize, fade: theme.chapterTitleFade, gap: theme.chapterTitleGap },
    subtitle: { font: theme.chapterSubtitleFont, align: theme.chapterSubtitleAlign, bold: theme.chapterSubtitleBold, italic: theme.chapterSubtitleItalic, style: theme.chapterSubtitleStyle, size: theme.chapterSubtitleSize, fade: theme.chapterSubtitleFade, gap: theme.chapterSubtitleGap },
  };
}

/** T‴7: whether the theme runs an image off the paper: only one at the page's top can, and only with the heading shown. */
export function bleeds(theme: Theme): boolean {
  return theme.chapterImageBleed && theme.chapterHeading && (theme.chapterImagePlace === 'above' || theme.chapterImagePlace === 'behind');
}

/** T‴2: the image reference a chapter's heading shows: the book's, or its own when each chapter has one; empty for none or with the heading off. */
export function headingImage(settings: { readonly theme: Theme; readonly image: string }): string {
  const { theme, image } = settings;
  if (!theme.chapterHeading) return '';
  return theme.chapterImagePerChapter ? image : theme.chapterImage;
}
